<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div class="k-page-wrapper">
	<h1 class="k-page-title">
		<k-btn icon x-large color="primary" class="mr-0" accessible-label="Return to admin main" @click="return_to_admin_main"><k-icon>fas fa-table</k-icon></k-btn>
		Resource Usage Report
	</h1>

	<div>
    <k-container fluid style="clear:both">
		<div class="d-flex align-end mb-6">
			<div>
				<div>Load data for<span v-if="!data_is_loaded">*</span>:</div>
				<div style="width:280px" class="mt-1"><k-select v-model="report_interval" :items="interval_list" solo hide-details label="" @input="interval_changed"></k-select></div>
			</div>
			<div v-if="report_interval==='custom'" style="width:300px" class="ml-2"><k-menu v-model="interval_chooser_menu_showing" :close-on-content-click="false" transition="scale-transition" offset-y min-width="300px" accessible-popover accessible-label="Choose report dates" content-id="admin-resource-usage-date-range-panel">
				<template v-slot:activator="{on, attrs}"><k-text-field background-color="#f8f8f8" solo hide-details v-model="interval_dates_display" label="Start Date – End Date" prepend-inner-icon="fas fa-calendar-week" prepend-decorative aria-label="Choose report dates" readonly v-on="on" v-bind="attrs"></k-text-field></template>
				<k-date-picker v-model="interval_dates" no-title scrollable range>
					<k-spacer></k-spacer>
					<k-btn text color="primary" @click="interval_chooser_menu_showing=false">Done</k-btn>
				</k-date-picker>
			</k-menu></div>
			<div class="ml-8">
				<div>Show resource usage by:</div>
				<div style="width:280px" class="mt-1"><k-select v-model="breakdown_by" :items="breakdown_by_options" label="" solo hide-details @input="update_headers" class="k-ru-breakout-menu"></k-select></div>
			</div>
			<k-btn class="ml-8 mb-1" v-if="resource_usage_rows.length==0" color="primary" @click="load_admin_resource_usage">Load Report Data <k-icon small class="ml-2">fas fa-arrow-circle-right</k-icon></k-btn>
			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" class="ml-4 mb-1" color="primary" outlined accessible-label="Download raw resource usage data" @click="download_raw_csv"><k-icon small>fas fa-download</k-icon></k-btn></template><div class="text-center">Download raw data<br>to CSV file</div></k-tooltip>
		</div>
		<!-- SF: Removing this note as this restriction is being removed -->
		<!-- <div v-if="!data_is_loaded" style="margin:-12px 0 18px 8px;font-size:14px"><i><b>*Note:</b> reports are currently limited to 7-day windows. This restriction will be lifted soon.</i></div> -->

		<div class="mb-1">Filter results by:</div>
		<k-row>
			<k-col sm="2.5">
				<k-select v-model="grade" :items="filter_grades" @input="filter_changed('grade')" label="Grade" solo outlined dense class="k-ru-filter-grade-menu"></k-select>
			</k-col>
			<k-col sm="2.5">
				<k-select v-model="subject" :items="filter_subjects" @input="filter_changed('subject')" label="Subject" solo outlined dense class="k-ru-filter-subject-menu"></k-select>
			</k-col>
			<k-col sm="2.5">
				<k-autocomplete v-model="lp" :items="filter_lps" @input="filter_changed('lp')" label="Learning Progression" solo outlined dense class="k-ru-filter-lp-menu"></k-autocomplete>
			</k-col>
			<!--<k-col sm="2.5">
				<k-autocomplete v-model="school" :items="filter_schools" @input="filter_changed('school')" label="School" solo outlined dense class="k-ru-filter-school-menu"></k-autocomplete>
			</k-col>-->
			<k-col v-if="!require_sign_in" sm="2.5">
				<k-autocomplete v-model="staff" :items="filter_staff_by_domain" @input="filter_changed('staff')" label="Staff" solo outlined dense class="k-ru-filter-staff-menu"></k-autocomplete>
			</k-col>
		</k-row>
    </k-container>
	</div>

	<div v-if="resource_usage_rows.length>0" style="clear:both">
		<div class="py-4 d-flex">
			<k-spacer/>
			<k-text-field
				v-model="raw_search"
				prepend-inner-icon="fa fa-search" prepend-decorative clearable clear-icon="fa fa-times-circle"
				aria-label="Search resource usage report"
				label="Search" single-line hide-details outlined dense background-color="#fff" style="flex:0 1 600px"
				@keyup="search_keyup"
				@click:clear="search=''"
			></k-text-field>
			<k-spacer/>
		</div>

		<div class="mb-4 d-flex align-center">
			<div class="pa-2 text-center light-blue lighten-4 elevation-2" style="border-radius:8px; flex:1 0 auto;"><b>Totals:</b>
				<span class="pl-4">Signed-in user resource access count: <b>{{this.total_staff_access_cnt.toLocaleString()}}</b></span>
				<span class="pl-8" v-if="show_signed_out_count">Unsigned-in resource access count: <b>{{this.total_signed_out_count.toLocaleString()}}</b></span>
			</div>
			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" class="ml-4" small fab color="primary" accessible-label="Download resource usage report" @click="download_report"><k-icon>fas fa-download</k-icon></k-btn></template><div class="text-center">Download report data<br>to CSV file</div></k-tooltip>
		</div>

		<k-data-table light
			:headers="headers"
			:items="filtered_report_rows"
			:sort-by="['staff_access_cnt']"
			:sort-desc="[true]"
			:must-sort="true"
			:footer-props="footer_options"
			class="k-admin-resource-usage-table"
		>
			<template v-slot:item="{ item }"><tr>
				<td class="text-left" v-html="item.breakout_0"></td>
				<td v-if="num_breakout_columns>1" class="text-left" v-html="item.breakout_1"></td>
				<td v-if="num_breakout_columns>2" class="text-left" v-html="item.breakout_2"></td>
				<td v-if="num_breakout_columns>3" class="text-left" v-html="item.breakout_3"></td>
				<td class="text-center">{{item.staff_access_cnt}}</td>
				<td class="text-center">{{item.user_cnt}}</td>
				<td v-if="show_signed_out_count" class="text-center">{{item.signed_out_count}}</td>
				<td v-if="show_gc_add_count" class="text-center">{{item.gc_add_cnt}}</td>
			</tr></template>
		</k-data-table>
	</div>
</div></template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	components: { },
	props: {},
	data() { return {
		// cubby api token from the get_cubby_token service ({ token, expires_at }).
		// cubby_token_request holds an in-flight request so parallel queries share one token fetch
		cubby_token: null,
		cubby_token_request: null,
		// icon shown in the table and label written to the csv for each guessed resource type (keys match site_config.asset_type_color)
		resource_type_icons: { assessment: 'fas fa-clipboard-list', lesson: 'fas fa-rectangle-list', sparkl_activity: 'fas fa-star', teacher_resource: 'fas fa-file' },
		resource_type_labels: { assessment: 'Assessment', lesson: 'Lesson', sparkl_activity: 'Student Activity', teacher_resource: 'Resource' },
		resource_usage_rows: [], // from ajax service
		report_rows: [],
		data_is_loaded: false,
		report_interval: 'custom',
		interval_list: [
			{value:'custom', text: 'Choose Report Dates'},
			{value:'day', text: 'Today'},
			{value:'week', text: 'This Week (since Saturday)'},
			// {value:'', text: 'Note: reports are currently limited to 7-day windows. This restriction will be lifted soon.'},
			// {value:'fortnight', text: 'Last 14 days'},
			// {value:'term', text: 'This Term'},
			// {value:'year', text: 'This Academic Year'},
		],
		interval_dates: ['',''],
		interval_dates_display: '',
		interval_chooser_showing: false,
		interval_chooser_menu_showing: false,

		show_signed_out_count: true,
		show_gc_add_count: true,
		num_breakout_columns: 0,
		headers: [],
      	breakdown_by: '',

		lp_list: [], // from ajax service
		domain_list: [],
		grade: '- All Grades -',
		subject: '- All Subjects -',
		lp: '- All LPs/Repositories -',
		//school: '- All Schools/Departments -',
		staff: '- All Users -',

		footer_options: {
			itemsPerPageOptions: [50,50,100,-1],
		},

		// since search filtering can take a while, use raw_search as the auto-updated value; then transfer to search when the user hits the enter key or clicks the search icon
		raw_search: '',
		search: '',

		limit_to_courses: [],
		//limit_to_schools: [],
	}},
	computed: {
		...mapState(['user_info', 'site_config']),
		...mapGetters([]),
		require_sign_in() { return this.site_config.require_sign_in == 'yes' },
		breakdown_by_options() {
			let arr = [
				{ text: 'Course/Resource Collection', value: 'lp_title' },
				{ text: 'Resource', value: 'resource_title' },
			]
			if (!this.require_sign_in) {
				arr.push({ text: 'District (Email Domain)', value: 'user_domain' })
			}
			return arr
		},

		filter_grades() {
			let arr = ['- All Grades -']
			for (let grade of this.$store.state.grades) {
				let text
				if (grade == 'PK') text = 'Pre-K'
				else if (grade == 'K') text = 'Kindergarten'
				else if (!isNaN(grade*1)) text = 'Grade ' + grade
				else text = grade

				arr.push({ value: grade, text: text})
			}
			return arr
		},
		filter_subjects() {
			let arr = ['- All Subjects -']
			for (let subject in this.$store.state.subjects) {
				arr.push({ value: subject, text: subject})
			}
			return arr
		},
		// update lp list as grades or subject filters changed
		filter_lps() {
			let arr = ['- All LPs/Repositories -']
			let lp_arr = this.lp_list

			if (this.subject !== '- All Subjects -') lp_arr = lp_arr.filter(lp => lp.subject == this.subject)
			if (this.grade !== '- All Grades -') {
				let g = (this.grade === 'K') ? 0 : this.grade*1
				lp_arr = lp_arr.filter(lp => g >= lp.grade_low && g <= lp.grade_high)
			}

			lp_arr.sort((a, b) => a.title < b.title ? -1 : (a.title > b.title ? 1 : 0))
			for (let lp of lp_arr) {
				// if this user can't edit the lp...
				if (false && !vapp.is_lp_admin(lp)) {
					// then unless the user is a principal or ap...
					// SF: Commenting this out as this getter does not exist so this was logging errors to console
					// if (!this.user_is_principal_or_ap) {
					// 	// don't let them see this lp's results
					// 	continue
					// }
				}
				arr.push({ value: lp.course_code, text: lp.title})
			}

			return arr
		},

		filter_staff_by_domain() {
			let arr = ['- All Users -']
			for (let item of this.domain_list) {
				if (!item || item == 'cglt.com') continue
				arr.push({ value: item, text: item })
			}
			// add 'Not Signed In' as first user in list
			arr.splice(1, 0,
				{ value: 'cglt.com',  text: 'Not Signed In'},
				{ value: '- All Signed-In Users -', text: 'All Signed-In Users'})
			return arr
		},

		search_reg_exp() {
			return new RegExp(this.search, 'i')
		},
		filtered_report_rows() {
			if (!this.search) {
				return this.report_rows
			}
			const filteredRows = this.report_rows.filter(row =>
				['breakout_0', 'breakout_1', 'breakout_2', 'breakout_3'].some(key =>
					row[key] && row[key].search(this.search_reg_exp) > -1
				)
			)
			return filteredRows
		},
		total_staff_access_cnt() {
			return this.filtered_report_rows.reduce((total, row) => total + row.staff_access_cnt, 0)
		},
		total_signed_out_count() {
			return this.filtered_report_rows.reduce((total, row) => total + row.signed_out_count, 0)
		},
		user_is_filtering_by_grade() {
			return this.grade !== '- All Grades -'
		},
		user_is_filtering_by_subject() {
			return this.subject !== '- All Subjects -'
		},
		user_is_filtering_by_course() {
			return this.lp !== '- All LPs/Repositories -'
		},
		user_is_filtering_by_domain() {
			return (this.staff !== '- All Users -' && this.staff !== '- All Signed-In Users -')
		},
		user_is_filtering_by_signed_in() {
			return this.staff === '- All Signed-In Users -'
		},
		user_is_filtering() {
			return this.user_is_filtering_by_grade || this.user_is_filtering_by_subject || this.user_is_filtering_by_course || this.user_is_filtering_by_domain || this.user_is_filtering_by_signed_in
		}
	},
	watch: {
		// if the dates change, do same as if interval selection is changed, set up for "Load Report Data" btn to show
		interval_dates() {	// deep?
			let s = ''
			if (this.interval_dates[0]) s += this.interval_dates[0].replace(/-/g, '/') + ' – '
			if (this.interval_dates[1]) s += this.interval_dates[1].replace(/-/g, '/')
			this.interval_dates_display = s

			this.resource_usage_rows = []
			this.report_rows = []
			this.data_is_loaded = false
		},
		// We also need to set up for Load Rerort Data if we change the 'breakdown_by' dropdown
		breakdown_by() {
			this.resource_usage_rows = []
			this.report_rows = []
			this.data_is_loaded = false
		},
	},
	created() {
		vapp.admin_resource_usage_component = this
		this.load_lp_list()

		this.breakdown_by = 'lp_title'
		// have to call update_headers in case we changed breakdown_by here
		this.update_headers()
	},
	mounted() {
	},
	methods: {
		// this service will need update if type of resource_usage.lp_id is changed to store lp_id rather than course_code
		load_lp_list() {
			let payload = { user_id: this.user_info.user_id }

			U.ajax('get_all_courses', payload, result=>{
				if (result.status != 'ok') {
					console.log('Error in get_all_courses ajax call'); vapp.ping(); return;
				}

				for (let i = 0; i < result.all_courses.length; ++i) {
					let c = result.all_courses[i]
					c.grade_low = empty(c.grade_low) ? -1 : ((c.grade_low === 'K') ? 0 : c.grade_low * 1)
					c.grade_high = empty(c.grade_high) ? -1 : ((c.grade_high === 'K') ? 0 : c.grade_high * 1)
					// // set PD grades to -1 for now
					// if (c.subject === 'PD') {c.grade_low = -1; c.grade_high = -1}
				}

				this.lp_list = result.all_courses
			})
		},

		interval_changed() {
			// when interval is changed, reset the data (but not the form parameters);
			// then the "Load Report Data" btn will appear; let the user click that to reload
			this.resource_usage_rows = []
			this.report_rows = []
			this.data_is_loaded = false
		},

		// start and end dates (YYYY-MM-DD) for the selected report interval
		report_date_range() {
			let today = new Date()
			if (this.report_interval === 'day') {
				return [date.format(today, 'YYYY-MM-DD'), date.format(today, 'YYYY-MM-DD')]
			} else if (this.report_interval === 'week') {
				// back to the most recent Saturday (today if it's Saturday), same as admin_get_resource_usage_data
				let day_of_week = today.getDay()
				let days_back = (day_of_week == 6) ? 0 : day_of_week + 1
				return [date.format(date.addDays(today, -days_back), 'YYYY-MM-DD'), date.format(today, 'YYYY-MM-DD')]
			}
			return [this.interval_dates[0], this.interval_dates[1]]
		},

		// cubby query payload for the selected dates and filters. The action and collection type filters match what
		// resource_usage_report counts. Custom dates are optional (the raw download can cover all dates), so only the
		// dates that are set become filters
		build_query(dimensions, include_ui_filters = true, measures = ['signed_in_access_count', 'signed_out_access_count']) {
			let [start_date, end_date] = this.report_date_range()
			let filters = []
			if (!empty(start_date)) filters.push({ field: 'date', operator: 'gte', value: start_date })
			if (!empty(end_date)) filters.push({ field: 'date', operator: 'lte', value: end_date })
			filters.push(
				{ field: 'collection_type', operator: 'neq', value: 'my' },
				{ field: 'action', operator: 'neq', value: 'view_temp_resource' },
				{ field: 'action', operator: 'neq', value: 'lti_chooser_choose' },
				{ field: 'action', operator: 'neq', value: 'view_sparkl_bank' },
			)

			if (include_ui_filters) {
				if (this.user_is_filtering_by_subject) filters.push({ field: 'subject', operator: 'eq', value: this.subject })
				if (this.user_is_filtering_by_course) filters.push({ field: 'course_code', operator: 'eq', value: this.lp })
				if (this.user_is_filtering_by_domain) filters.push({ field: 'domain', operator: 'eq', value: this.staff })
				// unsigned-in usage is recorded as unsignedin@cglt.com
				if (this.user_is_filtering_by_signed_in) filters.push({ field: 'domain', operator: 'neq', value: 'cglt.com' })
				// collections whose grade range includes the selected grade. cubby maps PK/PreK to -1 and K to 0
				if (this.user_is_filtering_by_grade) {
					let grade = (this.grade === 'PK') ? -1 : (this.grade === 'K') ? 0 : this.grade * 1
					filters.push(
						{ field: 'grade_low_num', operator: 'lte', value: grade },
						{ field: 'grade_high_num', operator: 'gte', value: grade },
					)
				}
			}

			return {
				measures: measures,
				dimensions: dimensions,
				filter_groups: [{ filters: filters }],
			}
		},

		// collection fields are always included so the subject/grade/collection columns work for every breakdown
		report_dimensions() {
			let collection_dimensions = ['collection', 'course_code', 'subject', 'grade_low', 'grade_high']
			if (this.breakdown_by == 'resource_title') return ['resource_id', 'resource_title', 'action', ...collection_dimensions]
			if (this.breakdown_by == 'user_domain') return ['domain', ...collection_dimensions]
			return collection_dimensions
		},

		// distinct user counts can't be summed across the report_dimensions rows that roll up into one report row,
		// so they're queried grouped by exactly the fields that make up the report row key (see report_row_key)
		user_count_dimensions() {
			if (this.breakdown_by == 'resource_title') return ['resource_id', 'course_code']
			if (this.breakdown_by == 'user_domain') return ['domain']
			return ['collection']
		},

		// key of the report row a cubby row rolls up into
		report_row_key(row) {
			// key on the collection too, so a resource used in multiple collections is listed once per collection
			if (this.breakdown_by == 'resource_title') return row.resource_id + ' -- ' + row.course_code
			if (this.breakdown_by == 'user_domain') return row.domain
			return row.collection
		},

		// returns a cubby token, reusing the cached one while it has more than a minute left
		async get_cubby_token(force = false) {
			if (!force && this.cubby_token && this.cubby_token.expires_at - 60 > Date.now() / 1000) {
				return this.cubby_token.token
			}

			if (!this.cubby_token_request) {
				this.cubby_token_request = U.ajax_promise('get_cubby_token', { user_id: this.user_info.user_id }).then(result => {
					if (result.status != 'ok') {
						throw new Error(result.status.replace(/^Error: /, ''))
					}
					this.cubby_token = { token: result.token, expires_at: result.expires_at }
					return this.cubby_token.token
				}).finally(() => {
					this.cubby_token_request = null
				})
			}
			return this.cubby_token_request
		},

		// posts a query to cubby with a token and returns the ok response; accept is 'application/json' or 'text/csv'
		async cubby_fetch(payload, accept = 'application/json', retried = false) {
			let token = await this.get_cubby_token(retried)
			let response = await fetch(this.site_config.cubby_url + '/api/query', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': accept, 'Authorization': `Bearer ${token}` },
				body: JSON.stringify(payload),
			})

			// the token expired or the secret changed; get a new token and try once more
			if (response.status === 401 && !retried) {
				return this.cubby_fetch(payload, accept, true)
			}

			if (response.status === 403) {
				throw new Error('You do not have access to this data')
			}
			if (!response.ok) {
				let result = await response.json().catch(() => ({}))
				throw new Error(result.message || `Cubby query failed (${response.status})`)
			}
			return response
		},

		async cubby_query(payload) {
			let response = await this.cubby_fetch(payload)
			let result = await response.json()
			return result.rows
		},

		// one row per usage as csv, for the selected dates and filters (no dates means all dates)
		async download_raw_csv() {
			if (this.report_interval === 'custom' && !empty(this.interval_dates[0]) && !empty(this.interval_dates[1]) && this.interval_dates[1] < this.interval_dates[0]) {
				this.$alert('The report end date is earlier than the start date.')
				return
			}

			let payload = this.build_query(['date', 'timestamp', 'email', 'domain', 'collection', 'course_code', 'subject', 'grade_low', 'grade_high', 'resource_title', 'resource_type', 'action'])
			payload.raw = true
			delete payload.measures
			payload.sort = [{ field: 'timestamp' }]

			U.loading_start()
			try {
				let response = await this.cubby_fetch(payload, 'text/csv')
				let blob = await response.blob()

				// an object url rather than U.download_file, whose data: url cuts the file off at the first #
				let url = URL.createObjectURL(blob)
				let anchor = document.createElement('a')
				anchor.href = url
				anchor.download = sr('Resource Usage Raw Data - $1.csv', date.format(new Date(), 'YYYY-MM-DD hh:mm'))
				document.body.appendChild(anchor) // required for firefox
				anchor.click()
				anchor.remove()
				URL.revokeObjectURL(url)
			} catch (error) {
				console.error(error)
				this.$alert('Error downloading raw resource usage data: ' + error.message)
			} finally {
				U.loading_stop()
			}
		},

		// get the usage rows and domain list from cubby
		async load_admin_resource_usage() {
			if (this.report_interval === 'custom') {
				if (empty(this.interval_dates[0])) {
					this.$alert('You must select a start date for report.')
					return
				}
				if (empty(this.interval_dates[1])) {
					this.$alert('You must select an end date for report.')
					return
				}
				if (this.interval_dates[1] < this.interval_dates[0]) {
					this.$alert('The report end date is earlier than the start date.')
					return
				}
			}

			U.loading_start()
			try {
				let [report_rows, domain_rows, user_count_rows] = await Promise.all([
					this.cubby_query(this.build_query(this.report_dimensions())),
					// the staff menu lists every domain in the date range, regardless of the other filters
					this.cubby_query(this.build_query(['domain'], false)),
					this.cubby_query(this.build_query(this.user_count_dimensions(), true, ['user_count'])),
				])
				this.resource_usage_rows = report_rows
				this.domain_list = domain_rows.map(row => row.domain)

				let user_counts = {}
				for (let row of user_count_rows) {
					user_counts[this.report_row_key(row)] = parseInt(row.user_count)
				}
				this.compile_report(user_counts)
			} catch (error) {
				console.error(error)
				this.$alert('Error loading resource usage data: ' + error.message)
			} finally {
				U.loading_stop()
			}
		},



		search_keyup(evt) {
			// if/when user hits enter key, set search to raw_search
			if (evt.key === "Enter"){
				this.search = this.raw_search
			}
		},

		// filters are applied by cubby, so a change needs a new query
		filter_changed(type) {
			if (!this.data_is_loaded) return
			this.load_admin_resource_usage()
		},

		// grade_low / grade_high are strings ('K', '6', ...); empty means no grade
		grade_value(grade) {
			if (empty(grade)) return -1
			return (grade == 'K') ? 0 : grade * 1
		},

		// resource type guessed from the action (this isn't perfect, but better than nothing)
		resource_type(row) {
			let title = row.resource_title ?? row.resource_id ?? ''
			// try to call out assessment for cureums that use assessment resources
			if (this.site_config.include_assessment_resources == 'yes' && title.search(/assessment/i) > -1) return 'assessment'
			if (row.action == 'view_lesson') return 'lesson'
			if (row.action == 'view_sparkl' || row.action == 'add_to_gc') return 'sparkl_activity'
			return 'teacher_resource'
		},

		// resource title with an icon for its type
		resource_breakout(row, type) {
			let title = row.resource_title ?? row.resource_id
			let icon = this.resource_type_icons[type]
			let color = this.site_config.asset_type_color[type]
			return `<i class="fa-fw ${icon} mr-2" style="font-size:18px; color:${color}"></i>${title}`
		},

		// roll the cubby rows (one per breakout value and collection) up into one report row per breakout value.
		// each usage is attributed to a single collection, so summing across collections doesn't double count.
		// user_counts maps report_row_key to the distinct user count for that row, which is looked up instead of summed
		compile_report(user_counts = {}) {
			let report_hash = {}

			for (let row of this.resource_usage_rows) {
				// usage outside of a collection isn't reported, same as the legacy report (course_code NOT IN ('0', 'default'))
				if (empty(row.course_code)) continue

				// for the grade(s) column; the grade filter itself is applied by cubby (see build_query)
				let gl = this.grade_value(row.grade_low)
				let gh = this.grade_value(row.grade_high)

				let row_key = this.report_row_key(row)
				let breakout_0, resource_type
				if (this.breakdown_by == 'resource_title') {
					resource_type = this.resource_type(row)
					breakout_0 = this.resource_breakout(row, resource_type)
				} else if (this.breakdown_by == 'user_domain') {
					// unsigned-in usage is recorded as unsignedin@cglt.com
					breakout_0 = (row.domain == 'cglt.com') ? 'Not Signed In' : row.domain
				} else {
					breakout_0 = row.collection
				}

				if (!(row_key in report_hash)) {
					report_hash[row_key] = {
						breakout_0: breakout_0,
						course_code: row.course_code,
						resource_id: row.resource_id,
						resource_title: row.resource_title ?? row.resource_id,
						resource_type: resource_type,
						staff_access_cnt: 0,
						user_cnt: user_counts[row_key] ?? 0,
						signed_out_count: 0,
						lp_titles: [],
						subjects: [],
						grades: [],
					}
				}

				let report_row = report_hash[row_key]
				report_row.staff_access_cnt += parseInt(row.signed_in_access_count)
				report_row.signed_out_count += parseInt(row.signed_out_access_count)

				if (report_row.lp_titles.indexOf(row.collection) === -1) report_row.lp_titles.push(row.collection)
				if (row.subject && report_row.subjects.indexOf(row.subject) === -1) report_row.subjects.push(row.subject)
				for (let i = gl; i <= gh; ++i) {
					if (report_row.grades.indexOf(i) === -1) report_row.grades.push(i)
				}
			}
			this.report_rows = Object.values(report_hash)

			for (let row of this.report_rows) {
				// sort grades array before formatting grade ranges
				row.grades.sort((a,b) => a - b)

				// create formatted values to show in columns
				if (this.num_breakout_columns > 1) row.breakout_1 = this.format_column_value(row, 1)
				if (this.num_breakout_columns > 2) row.breakout_2 = this.format_column_value(row, 2)
				if (this.num_breakout_columns > 3) row.breakout_3 = this.format_column_value(row, 3)
			}

			this.data_is_loaded = true
		},

		// we do some different things to format
		format_column_value(row, col_index) {
			let col_name = this.headers[col_index].value

			if (col_name === 'grade') {
				//if (row.breakout_0 != 'HMH activities') return
				return this.grade_range_str(row['grades'])
			} else if (col_name == 'resource_title') {
				return row[col_name + 's'].join(', ')
			} else if (col_name == 'subject' || col_name == 'lp_title') {
				return '<nobr>' + row[col_name + 's'].join(',</nobr><br><nobr>') + '</nobr>'
			} else {
				return 'ERROR'
			}
		},

		// plain-text value of a report column for the csv
		csv_column_value(row, col_name) {
			if (col_name == 'breakout_0') return (this.breakdown_by == 'resource_title') ? row.resource_title : row.breakout_0
			if (col_name == 'grade') return this.grade_range_str(row.grades)
			if (col_name == 'subject' || col_name == 'lp_title') return row[col_name + 's'].join(', ')
			return row[col_name]
		},

		// assemble string for grade(s) display
		// there can be disjoint grade ranges
		grade_range_str(grades) {
			if (grades && grades[0] == -1) return ''
			if (grades.length === 1) return (grades[0] === 0) ? 'K' : grades[0]+''

			// snippet to convert array of grades to array of range strings
			// incoming array of grades must be sorted
			// https://stackoverflow.com/questions/2270910/how-to-reduce-consecutive-integers-in-an-array-to-hyphenated-range-expressions
			var ranges = [], rstart, rend
			for (var i = 0; i < grades.length; i++) {
				rstart = grades[i]
				rend = rstart

				while (grades[i + 1] - grades[i] === 1) {
					rend = grades[i + 1] // increment the index if the numbers sequential
					i++
				}
				rstart = (rstart === 0) ? 'K' : rstart
				ranges.push(rstart === rend ? rstart+'' : rstart + '-' + rend)
			}

			return ranges.join(', ')
		},

		lp_link(course_code, lp_title) {
			return sr('<a href="javascript:vapp.admin_resource_usage_component.open_lp($1)">$2</a>', course_code, lp_title)
		},
		open_lp(course_code) {
			if (course_code*1 < 90000) {
				this.$router.push({ path: sr('/course/lp/$1/0/0', course_code) })
			} else {
				this.$router.push({ path: sr('/repo/$1/0/0', course_code) })
			}
		},
		resource_link(resource_id, resource_title) {
			return sr('<a href="javascript:vapp.admin_resource_usage_component.open_resource(\'$1\')">$2</a>', resource_id, resource_title)
		},
		open_resource() {

		},

		// update report columns as breakout column is selected
		update_headers(value) {
			if (this.breakdown_by == 'lp_title') {
				this.headers = [
					{ text: 'Course/Resource Collection', align: 'left', sortable: true, value:'breakout_0' },
					{ text: 'Subject', align: 'left', sortable: false, value:'subject' },
					{ text: 'Grade(s)', align: 'left', sortable: false, value:'grade' },
					{ text: 'Access Count', align: 'center', sortable: true, value:'staff_access_cnt' },
				]
				this.num_breakout_columns = 3

			} else if (this.breakdown_by == 'resource_title') {
				this.headers = [
					{ text: 'Resource', align: 'left', sortable: true, value:'breakout_0' },
					{ text: 'Subject', align: 'left', sortable: false, value:'subject' },
					{ text: 'Grade(s)', align: 'left', sortable: false, value:'grade' },
					{ text: 'Course/Resource Collection', align: 'left', sortable: false, value:'lp_title' },
					{ text: 'Access Count', align: 'center', sortable: true, value:'staff_access_cnt' },
				]
				this.num_breakout_columns = 4

			} else if (this.breakdown_by == 'user_domain') {
				this.headers = [
					{ text: 'User Domain', align: 'left', sortable: true, value:'breakout_0' },
					{ text: 'Subject', align: 'left', sortable: false, value:'subject' },
					{ text: 'Grade(s)', align: 'left', sortable: false, value:'grade' },
					{ text: 'Course/Resource Collection', align: 'left', sortable: false, value:'lp_title' },
					{ text: 'Access Count', align: 'center', sortable: true, value:'staff_access_cnt' },
				]
				this.num_breakout_columns = 4

			}
			// that should cover all possibilities

			this.headers.push({ text: 'Unique User Count', align: 'center', sortable: true, value:'user_cnt' })

			if (this.site_config.require_sign_in != 'yes') {
				this.headers.push({ text: 'Not-Signed-In Count', align: 'center', sortable: true, value:'signed_out_count' })
				this.show_signed_out_count = true
			} else {
				this.show_signed_out_count = false
			}

			// TODO: get this working...
			if (false) {
				this.headers.push({ text: 'GC Add Count', align: 'center', sortable: true, value:'gc_add_cnt' })
				this.show_gc_add_count = true
			} else {
				this.show_gc_add_count = false
			}
		},

		download_report() {
			let filename = sr('Resource Usage Report - $1.csv', date.format(new Date(), 'YYYY-MM-DD hh:mm'))

			let table = []

			// for the resource report, the csv gets a resource type column in place of the table's icon
			let include_resource_type = (this.breakdown_by == 'resource_title')

			// create header row
			let header_row = []
			for (let col of this.headers) {
				header_row.push(col.text)
				if (include_resource_type && col.value == 'breakout_0') header_row.push('Resource Type')
			}

			// create table rows from the raw row data (the table's breakout columns hold html)
			for (let item of this.filtered_report_rows) {
				let row = []
				for (let col of this.headers) {
					row.push(this.csv_column_value(item, col.value))
					if (include_resource_type && col.value == 'breakout_0') row.push(this.resource_type_labels[item.resource_type])
				}
				table.push(row)
			}

			// sort by staff access count descending
			let count_col = header_row.indexOf('Access Count')
			table.sort((a,b) => b[count_col] - a[count_col])

			// add the header to the top of the file
			table.unshift(header_row)

			U.download_file(CSV.stringify(table), filename)
		},

		return_to_admin_main() {
			this.$router.push({ path: '/' })
		},
	},
}
</script>

<style lang="scss">
	.k-admin-resource-usage-table {
		th {
			white-space:nowrap;
		}
	}
</style>
